import React, { useEffect, useState } from "react";
import { createPropertyDefinition, deletePropertyDefinition, getAssetTypes, getPropertyDefinitions, updatePropertyDefinition } from "../services/assetTypeService";
import "../styles/Properties.css";

const emptyForm = {
  propertyName: "",
  dataType: "string",
  isRequired: false,
  displayOrder: 0,
  is_public: true,
  assetTypeIds: [],
};

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingPropertyIds, setEditingPropertyIds] = useState([]);
  const [editingPropertyRecords, setEditingPropertyRecords] = useState([]);
  const [assetTypeMenuOpen, setAssetTypeMenuOpen] = useState(false);
  const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const loadData = async () => {
    try {
      const [propertyData, assetTypeData] = await Promise.all([getPropertyDefinitions(), getAssetTypes()]);
      setProperties(propertyData);
      setAssetTypes(assetTypeData);
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load properties.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const startCreate = () => {
    setEditingId(null);
    setEditingPropertyIds([]);
    setEditingPropertyRecords([]);
    setForm(emptyForm);
    setAssetTypeMenuOpen(false);
    setShowForm(true);
    setError("");
  };

  const startEdit = (propertyGroup) => {
    const property = propertyGroup.properties ? propertyGroup.properties[0] : propertyGroup;
    const propertyIds = propertyGroup.properties ? propertyGroup.properties.map((item) => item.id) : [property.id];
    const propertyRecords = propertyGroup.properties
      ? propertyGroup.properties.map((item) => ({ id: item.id, assetTypeId: item.assetTypeId }))
      : [{ id: property.id, assetTypeId: property.assetTypeId }];
    setEditingId(property.id);
    setEditingPropertyIds(propertyIds);
    setEditingPropertyRecords(propertyRecords);
    setShowForm(true);
    setAssetTypeMenuOpen(false);
    setForm({
      propertyName: property.propertyName || "",
      dataType: property.dataType || "string",
      isRequired: Boolean(property.isRequired),
      displayOrder: property.displayOrder || 0,
      is_public: property.isPublic !== false,
      assetTypeIds: propertyGroup.properties ? propertyGroup.properties.map((item) => item.assetTypeId).filter(Boolean) : property.assetTypeId ? [property.assetTypeId] : [],
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.assetTypeIds.length) {
      setError("Choose at least one asset type.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const propertyFields = {
        propertyName: form.propertyName,
        dataType: form.dataType,
        isRequired: form.isRequired,
        displayOrder: Number(form.displayOrder),
        is_public: form.is_public,
      };

      if (editingId) {
        const existingRecords = editingPropertyRecords.length ? editingPropertyRecords : [{ id: editingId, assetTypeId: form.assetTypeIds[0] }];
        const selectedAssetTypeIds = new Set(form.assetTypeIds);
        const existingAssetTypeIds = new Set(existingRecords.map((record) => record.assetTypeId));
        const recordsToUpdate = existingRecords.filter((record) => selectedAssetTypeIds.has(record.assetTypeId));
        const recordsToDelete = existingRecords.filter((record) => !selectedAssetTypeIds.has(record.assetTypeId));
        const assetTypeIdsToCreate = form.assetTypeIds.filter((assetTypeId) => !existingAssetTypeIds.has(assetTypeId));

        await Promise.all([
          ...recordsToUpdate.map((record) => updatePropertyDefinition(record.id, propertyFields)),
          ...recordsToDelete.map((record) => deletePropertyDefinition(record.id)),
          ...assetTypeIdsToCreate.map((assetTypeId) => createPropertyDefinition({ ...propertyFields, assetTypeId })),
        ]);
      } else {
        await Promise.all(form.assetTypeIds.map((assetTypeId) => createPropertyDefinition({ ...propertyFields, assetTypeId })));
      }
      setShowForm(false);
      setEditingId(null);
      setEditingPropertyIds([]);
      setEditingPropertyRecords([]);
      setForm(emptyForm);
      await loadData();
    } catch (requestError) {
      const messages = requestError.response?.data?.messages;
      setError(messages?.join(", ") || requestError.response?.data?.message || "Unable to save property.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (propertyGroup) => {
    const groupProperties = propertyGroup.properties || [propertyGroup];
    const propertyName = propertyGroup.name || propertyGroup.propertyName;
    if (!window.confirm(`Delete property '${propertyName}' from all assigned asset types?`)) return;
    setDeletingId(propertyGroup.id || propertyGroup.id);
    setError("");
    try {
      await Promise.all(groupProperties.map((property) => deletePropertyDefinition(property.id)));
      if (editingPropertyIds.some((id) => groupProperties.some((property) => property.id === id))) {
        setShowForm(false);
        setEditingId(null);
        setEditingPropertyIds([]);
        setEditingPropertyRecords([]);
      }
      await loadData();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to delete property.");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleProperty = (propertyName) => {
    setExpandedAssetTypes((current) => ({
      ...current,
      [propertyName]: !current[propertyName],
    }));
  };

  const groupedProperties = properties.reduce((groups, property) => {
    const propertyKey = property.propertyName || property.id;

    if (!groups[propertyKey]) {
      groups[propertyKey] = {
        id: propertyKey,
        name: property.propertyName || "Unnamed property",
        dataType: property.dataType,
        properties: [],
      };
    }

    groups[propertyKey].properties.push(property);
    return groups;
  }, {});

  if (loading)
    return (
      <div className="loading loading-with-spinner">
        <span className="spinner" aria-hidden="true" /> Loading properties...
      </div>
    );

  return (
    <div className="properties-page">
      <div className="properties-header">
        <div>
          <p className="eyebrow">Schema management</p>
          <h1>Properties</h1>
          <p className="properties-intro">Create reusable fields and attach them to one or more asset types.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={startCreate}>
          + New Property
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <form className="property-editor" onSubmit={handleSubmit}>
          <div className="property-editor-heading">
            <div>
              <h2>{editingId ? "Edit property" : "New property"}</h2>
              <p>Select one or more asset types. A separate definition is created for each.</p>
            </div>
            {editingId && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={startCreate}>
                Cancel edit
              </button>
            )}
          </div>
          <div className="property-editor-grid">
            <div className="form-group">
              <label htmlFor="propertyName">Property name *</label>
              <input id="propertyName" name="propertyName" value={form.propertyName} onChange={handleChange} maxLength="100" required placeholder="e.g. Serial number" />
            </div>
            <div className="form-group">
              <label htmlFor="dataType">Data type *</label>
              <select id="dataType" name="dataType" value={form.dataType} onChange={handleChange} required>
                <option value="string">String</option>
                <option value="text">Text</option>
                <option value="integer">Integer</option>
                <option value="decimal">Decimal</option>
                <option value="date">Date</option>
                <option value="boolean">Boolean</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="displayOrder">Display order</label>
              <input id="displayOrder" name="displayOrder" type="number" min="0" value={form.displayOrder} onChange={handleChange} />
            </div>
          </div>
          <div className="asset-type-picker">
            <span className="picker-label">Asset types *</span>
            <div className="asset-type-dropdown">
              <button type="button" className="asset-type-dropdown-trigger" onClick={() => setAssetTypeMenuOpen((open) => !open)} aria-expanded={assetTypeMenuOpen}>
                {form.assetTypeIds.length
                  ? assetTypes
                      .filter((assetType) => form.assetTypeIds.includes(assetType.id))
                      .map((assetType) => assetType.name)
                      .join(", ")
                  : "Select asset types"}
                <span aria-hidden="true">&#9662;</span>
              </button>
              {assetTypeMenuOpen && (
                <div className="asset-type-dropdown-menu">
                  {assetTypes.map((assetType) => (
                    <label className="asset-type-dropdown-option" key={assetType.id}>
                      <input
                        type="checkbox"
                        checked={form.assetTypeIds.includes(assetType.id)}
                        onChange={() =>
                          setForm((current) => ({
                            ...current,
                            assetTypeIds: current.assetTypeIds.includes(assetType.id)
                              ? current.assetTypeIds.filter((id) => id !== assetType.id)
                              : [...current.assetTypeIds, assetType.id],
                          }))
                        }
                      />
                      <span>{assetType.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {!assetTypes.length && <p className="small">Create an asset type before adding properties.</p>}
          </div>
          <div className="property-flags">
            <label>
              <input name="isRequired" type="checkbox" checked={form.isRequired} onChange={handleChange} /> Required
            </label>
            <label>
              <input name="is_public" type="checkbox" checked={form.is_public} onChange={handleChange} /> Public
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving || !assetTypes.length}>
              {saving ? "Saving..." : editingId ? "Update Property" : "Create Property"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <section className="property-list-section">
        <div className="section-heading">
          <h2>Property definitions</h2>
          <span>{properties.length} total</span>
        </div>
        {properties.length === 0 ? (
          <div className="empty-state">
            <p>No property definitions yet.</p>
          </div>
        ) : (
          <div className="property-groups">
            {Object.values(groupedProperties).map((group) => {
              const isExpanded = expandedAssetTypes[group.id] !== false;
              const definition = group.properties[0];

              return (
                <section className="property-group" key={group.id}>
                  <div className="property-group-header">
                    <button type="button" className="property-group-toggle" onClick={() => toggleProperty(group.id)} aria-expanded={isExpanded}>
                      <span className="property-group-title">
                        <span className="property-group-chevron" aria-hidden="true">
                          {isExpanded ? "-" : "+"}
                        </span>
                        <strong>{group.name}</strong>
                        <span className="data-type-badge">{group.dataType}</span>
                      </span>
                      <span className="property-group-count">
                        {group.properties.length} {group.properties.length === 1 ? "asset type" : "asset types"}
                      </span>
                    </button>
                    <div className="property-group-meta">
                      <span>{definition.isRequired ? "Required" : "Optional"}</span>
                      <span>{definition.isPublic === false ? "Private" : "Public"}</span>
                      <span>Order: {definition.displayOrder || 0}</span>
                      <span className="property-group-actions">
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => startEdit(group)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(group)} disabled={deletingId === group.id}>
                          {deletingId === group.id ? "Deleting..." : "Delete"}
                        </button>
                      </span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="property-list">
                      {group.properties.map((property) => (
                        <article className="property-card" key={property.id}>
                          <div className="property-card-main">
                            <span className="property-card-asset-type">{property.AssetType?.name || "Unknown asset type"}</span>
                          </div>
                          <div className="property-card-meta">Order: {property.displayOrder || 0}</div>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default PropertiesPage;
